import { Check, Eye, RefreshCw, Search, Trash2, Wifi, WifiOff, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Error } from "../../components/common/Error";
import { HHTabs, TabsMap } from "../../components/common/Tabs";
import { MarketNavigation } from "../../components/common/MarketNavigation";
import { API_ENDPOINTS } from "../../constants/common";
import { RootState } from "../../store";
import { selectUserData, selectUserInfo, selectUserToken } from "../../store/selectors";
import { apiClient } from "../../utils";
import { resolveListingTotalPrice } from "../../utils/listingPrice";

type ListingType = "house" | "land_plot" | "apartment" | "commercial";
type MarketType = "sale" | "rent";
type ModerationStatus = "pending" | "approved" | "rejected";
type ModerationQueueTab = Extract<ModerationStatus, "pending" | "rejected">;
type SortKey =
  | "moderation_submitted_at"
  | "updated_at"
  | "created_at"
  | "price_sqm"
  | "price_monthly"
  | "rent_term"
  | "agent_commission_percent"
  | "agent_commission_amount";

type ModerationListing = {
  id: number | string;
  type: ListingType;
  owner_id?: number | string;
  author_id?: number | string;
  status_id?: number | string;
  title: string;
  market_type: MarketType;
  board_category: string;
  board_subcategory?: string | null;
  image_url: string | null;
  area_sqm: number | string | null;
  area_site: number | string | null;
  price_sqm: number | string | null;
  price_total?: number | string | null;
  price_monthly: number | string | null;
  moderation_status: ModerationStatus;
  moderation_reason?: string | null;
  moderation_submitted_at?: string | null;
  moderated_at?: string | null;
  moderated_by?: number | string | null;
  updated_at?: string | null;
  created_at?: string | null;
};

type ListingPagination = {
  limit: number;
  has_more: boolean;
  next_cursor: string | null;
};

type UserNode = {
  abilities?: string[];
  token?: string | null;
  data?: UserNode;
  profile?: UserNode;
  user?: UserNode;
} | null;

type Props = {
  user: UserNode;
  userData: UserNode;
  userToken?: string | null;
};

const PAGE_LIMIT = 30;

const TYPE_LABELS: Record<ListingType, string> = {
  house: "Дом",
  land_plot: "Участок",
  apartment: "Квартира",
  commercial: "Коммерция",
};

const MARKET_LABELS: Record<MarketType, string> = {
  sale: "Продажа",
  rent: "Аренда",
};

const CATEGORY_LABELS: Record<string, string> = {
  houses: "Дома",
  land_plots: "Земельные участки",
  assignments: "Переуступки",
  secondary: "Вторичное жильё",
  apartments: "Квартиры",
  commercial: "Коммерция",
  dnt: "ДНТ",
  snt: "СНТ",
  izhs: "ИЖС",
  parking: "Паркинг",
};

const MODERATION_LABELS: Record<ModerationStatus, string> = {
  pending: "На модерации",
  approved: "Одобрено",
  rejected: "Отклонено",
};

const SORT_LABELS: Record<SortKey, string> = {
  moderation_submitted_at: "Дата отправки",
  updated_at: "Обновление",
  created_at: "Создание",
  price_sqm: "Цена за м²",
  price_monthly: "Аренда в месяц",
  rent_term: "Срок аренды",
  agent_commission_percent: "Комиссия, %",
  agent_commission_amount: "Комиссия, ₽",
};

const hasUserAbility = (ability: string, ...nodes: UserNode[]): boolean => {
  const stack = nodes.filter(Boolean) as NonNullable<UserNode>[];
  const visited = new Set<NonNullable<UserNode>>();

  while (stack.length) {
    const node = stack.pop();
    if (!node || visited.has(node)) continue;
    visited.add(node);

    if (Array.isArray(node.abilities) && node.abilities.includes(ability)) {
      return true;
    }

    if (node.data) stack.push(node.data);
    if (node.profile) stack.push(node.profile);
    if (node.user) stack.push(node.user);
  }

  return false;
};

const getCookieValue = (name: string): string => {
  const rawValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return rawValue ? decodeURIComponent(rawValue) : "";
};

const normalizeAuthToken = (value: unknown): string => {
  const token = String(value || "").trim();
  return token && token !== "undefined" && token !== "null" ? token : "";
};

const getListingKey = (item: Pick<ModerationListing, "type" | "id">) => `${item.type}:${item.id}`;

const formatMoney = (value: unknown): string | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(parsed)} ₽`;
};

const formatArea = (item: ModerationListing): string | null => {
  const raw = item.type === "land_plot" ? item.area_site : item.area_sqm;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  if (item.type === "land_plot") {
    return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(parsed / 100)} сот.`;
  }
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(parsed)} м²`;
};

const getListingPrice = (item: ModerationListing): string => {
  if (item.market_type === "rent") {
    const monthly = formatMoney(item.price_monthly);
    return monthly ? `${monthly}/мес` : "Договорная";
  }

  const area = Number(item.type === "land_plot" ? item.area_site : item.area_sqm);
  const priceSqm = Number(item.price_sqm);
  const total = resolveListingTotalPrice({
    area,
    pricePerSqm: priceSqm,
    explicitTotalPrice: item.price_total,
  });
  return formatMoney(total) || formatMoney(item.price_sqm) || "Договорная";
};

const formatDate = (value: unknown): string => {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const normalizeSseUrl = (baseUrl: string, query: URLSearchParams) => `${baseUrl}?${query.toString()}`;

const buildAdminQuery = ({
  filters,
  cursor,
}: {
  filters: {
    moderationStatus: ModerationQueueTab;
    type: ListingType | "all";
    marketType: MarketType | "all";
    boardCategory: string;
    ownerId: string;
    search: string;
    sort: SortKey;
    sortDirection: "asc" | "desc";
  };
  cursor?: string | null;
}) => {
  const query = new URLSearchParams({
    moderation_status: filters.moderationStatus,
    sort: filters.sort,
    sort_direction: filters.sortDirection,
    limit: String(PAGE_LIMIT),
  });

  if (filters.type !== "all") query.set("type", filters.type);
  if (filters.marketType !== "all") query.set("market_type", filters.marketType);
  if (filters.boardCategory.trim()) query.set("board_category", filters.boardCategory.trim());
  if (filters.ownerId.trim()) query.set("owner_id", filters.ownerId.trim());
  if (filters.search.trim()) query.set("q", filters.search.trim());
  if (cursor) query.set("cursor", cursor);

  return query;
};

const _ClassifiedsModeration: React.FC<Props> = ({ user, userData, userToken }) => {
  const navigate = useNavigate();
  const canManageListings = hasUserAbility("manager", userData, user);
  const authToken = normalizeAuthToken(userToken || userData?.token || getCookieValue("userToken"));
  const requestIdRef = useRef(0);
  const streamAbortRef = useRef<AbortController | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [items, setItems] = useState<ModerationListing[]>([]);
  const [pagination, setPagination] = useState<ListingPagination>({
    limit: PAGE_LIMIT,
    has_more: false,
    next_cursor: null,
  });
  const [pending, setPending] = useState(true);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const [streamStatus, setStreamStatus] = useState<"idle" | "connecting" | "connected" | "error">("idle");
  const [actionPendingKeys, setActionPendingKeys] = useState<Set<string>>(new Set());
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [selectedListingKeys, setSelectedListingKeys] = useState<Set<string>>(new Set());
  const [bulkDeletePending, setBulkDeletePending] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
  const [rejectKey, setRejectKey] = useState<string | null>(null);
  const [rejectReasons, setRejectReasons] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState({
    moderationStatus: "pending" as ModerationQueueTab,
    type: "all" as ListingType | "all",
    marketType: "all" as MarketType | "all",
    boardCategory: "",
    ownerId: "",
    search: "",
    sort: "moderation_submitted_at" as SortKey,
    sortDirection: "desc" as "asc" | "desc",
  });

  const selectedListings = useMemo(
    () => items.filter((item) => selectedListingKeys.has(getListingKey(item))),
    [items, selectedListingKeys],
  );
  const selectableListings = useMemo(() => items.slice(0, 100), [items]);
  const allSelectableListingsSelected =
    selectableListings.length > 0 &&
    selectableListings.every((item) => selectedListingKeys.has(getListingKey(item)));

  useEffect(() => {
    const availableKeys = new Set(items.map(getListingKey));
    setSelectedListingKeys((current) => {
      const next = new Set(Array.from(current).filter((key) => availableKeys.has(key)).slice(0, 100));
      if (next.size === current.size && Array.from(next).every((key) => current.has(key))) return current;
      return next;
    });
  }, [items]);

  const toggleListingSelection = useCallback(
    (item: ModerationListing) => {
      if (bulkDeletePending || actionPendingKeys.has(getListingKey(item))) return;
      const key = getListingKey(item);
      if (!selectedListingKeys.has(key) && selectedListingKeys.size >= 100) {
        setBulkDeleteError("За один раз можно удалить не более 100 объявлений.");
        return;
      }

      setBulkDeleteError(null);
      setSelectedListingKeys((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [actionPendingKeys, bulkDeletePending, selectedListingKeys],
  );

  const toggleAllListingSelection = useCallback(() => {
    if (bulkDeletePending) return;
    setBulkDeleteError(null);
    setSelectedListingKeys((current) => {
      const next = new Set(current);
      if (allSelectableListingsSelected) {
        selectableListings.forEach((item) => next.delete(getListingKey(item)));
      } else {
        selectableListings.forEach((item) => {
          const key = getListingKey(item);
          if (!actionPendingKeys.has(key) && next.size < 100) next.add(key);
        });
      }
      return next;
    });
  }, [actionPendingKeys, allSelectableListingsSelected, bulkDeletePending, selectableListings]);

  const deleteSelectedListings = useCallback(async () => {
    if (!selectedListings.length || bulkDeletePending) return;

    const shouldDelete = window.confirm(
      `Удалить выбранные объявления (${selectedListings.length})? Это действие нельзя отменить.`,
    );
    if (!shouldDelete) return;

    const deletedKeys = new Set(selectedListings.map(getListingKey));
    setBulkDeletePending(true);
    setBulkDeleteError(null);

    try {
      await apiClient.delete(API_ENDPOINTS.classifiedsListings, {
        data: {
          listings: selectedListings.map((item) => {
            const numericId = Number(item.id);
            return {
              type: item.type,
              id: Number.isSafeInteger(numericId) && numericId > 0 ? numericId : item.id,
            };
          }),
        },
      });

      setItems((current) => current.filter((item) => !deletedKeys.has(getListingKey(item))));
      setSelectedListingKeys(new Set());
      setRejectKey((current) => (current && deletedKeys.has(current) ? null : current));
      setActionErrors((current) =>
        Object.fromEntries(Object.entries(current).filter(([key]) => !deletedKeys.has(key))),
      );
    } catch (requestError: any) {
      setBulkDeleteError(requestError?.response?.data?.error || "Не удалось удалить выбранные объявления");
    } finally {
      setBulkDeletePending(false);
    }
  }, [bulkDeletePending, selectedListings]);

  const setItemActionPending = useCallback((key: string, isPending: boolean) => {
    setActionPendingKeys((current) => {
      const next = new Set(current);
      if (isPending) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const loadListings = useCallback(
    async (cursor: string | null = null, options: { silent?: boolean } = {}) => {
      if (!canManageListings || !authToken) {
        setPending(false);
        return;
      }

      const append = Boolean(cursor);
      const requestId = ++requestIdRef.current;
      if (append) {
        setLoadMorePending(true);
        setLoadMoreError(null);
      } else if (!options.silent) {
        setPending(true);
        setError(null);
        setLoadMoreError(null);
      }

      try {
        const query = buildAdminQuery({ filters, cursor });
        const response = await apiClient.get(`${API_ENDPOINTS.classifiedsModerationListings}?${query.toString()}`);
        if (requestId !== requestIdRef.current) return;

        const nextItems: ModerationListing[] = Array.isArray(response?.data?.data) ? response.data.data : [];
        const nextPagination = response?.data?.meta?.pagination;
        setItems((current) => {
          if (!append) return nextItems;
          const known = new Set(current.map(getListingKey));
          return [...current, ...nextItems.filter((item) => !known.has(getListingKey(item)))];
        });
        setPagination({
          limit: Number(nextPagination?.limit) || PAGE_LIMIT,
          has_more: Boolean(nextPagination?.has_more),
          next_cursor: typeof nextPagination?.next_cursor === "string" ? nextPagination.next_cursor : null,
        });
      } catch (requestError: any) {
        if (requestId !== requestIdRef.current) return;
        const message = requestError?.response?.data?.error || "Не удалось загрузить очередь модерации";
        if (append) setLoadMoreError(message);
        else setError(message);
      } finally {
        if (requestId === requestIdRef.current) {
          if (append) setLoadMorePending(false);
          else setPending(false);
        }
      }
    },
    [authToken, canManageListings, filters],
  );

  useEffect(() => {
    void loadListings();
  }, [loadListings]);

  const scheduleSilentRefresh = useCallback(() => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      void loadListings(null, { silent: true });
    }, 300);
  }, [loadListings]);

  useEffect(() => {
    if (!realtimeEnabled || !canManageListings || !authToken) {
      setStreamStatus("idle");
      streamAbortRef.current?.abort();
      return;
    }

    const controller = new AbortController();
    streamAbortRef.current = controller;
    setStreamStatus("connecting");

    const streamQuery = new URLSearchParams({
      moderation_status: filters.moderationStatus,
      limit: String(PAGE_LIMIT),
    });
    if (filters.type !== "all") streamQuery.set("type", filters.type);

    const connect = async () => {
      try {
        const response = await fetch(normalizeSseUrl(API_ENDPOINTS.classifiedsModerationStream, streamQuery), {
          headers: { Authorization: `Bearer ${authToken}` },
          signal: controller.signal,
        });
        if (!response.ok || !response.body) {
          throw new Error("Stream unavailable");
        }

        setStreamStatus("connected");
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!controller.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split("\n\n");
          buffer = chunks.pop() || "";
          for (const chunk of chunks) {
            if (chunk.includes("data:")) {
              scheduleSilentRefresh();
            }
          }
        }
      } catch {
        if (!controller.signal.aborted) {
          setStreamStatus("error");
        }
      }
    };

    void connect();

    return () => {
      controller.abort();
      if (streamAbortRef.current === controller) streamAbortRef.current = null;
    };
  }, [authToken, canManageListings, filters.moderationStatus, filters.type, realtimeEnabled, scheduleSilentRefresh]);

  useEffect(
    () => () => {
      streamAbortRef.current?.abort();
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    },
    [],
  );

  const approveListing = useCallback(
    async (item: ModerationListing) => {
      const key = getListingKey(item);
      if (actionPendingKeys.has(key)) return;

      setItemActionPending(key, true);
      setActionErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });

      try {
        await apiClient.post(API_ENDPOINTS.classifiedsApproveModeration(item.type, item.id));
        setItems((current) => current.filter((row) => getListingKey(row) !== key));
      } catch (requestError: any) {
        setActionErrors((current) => ({
          ...current,
          [key]: requestError?.response?.data?.error || "Не удалось одобрить объявление",
        }));
      } finally {
        setItemActionPending(key, false);
      }
    },
    [actionPendingKeys, setItemActionPending],
  );

  const rejectListing = useCallback(
    async (item: ModerationListing) => {
      const key = getListingKey(item);
      if (actionPendingKeys.has(key)) return;

      setItemActionPending(key, true);
      setActionErrors((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });

      try {
        const reason = String(rejectReasons[key] || "").trim();
        const response = await apiClient.post(API_ENDPOINTS.classifiedsRejectModeration(item.type, item.id), {
          reason: reason || null,
        });
        const nextItem = response?.data?.data;
        setRejectKey(null);
        setItems((current) =>
          filters.moderationStatus === "rejected"
            ? current.map((row) => (getListingKey(row) === key ? { ...row, ...nextItem } : row))
            : current.filter((row) => getListingKey(row) !== key),
        );
      } catch (requestError: any) {
        setActionErrors((current) => ({
          ...current,
          [key]: requestError?.response?.data?.error || "Не удалось отклонить объявление",
        }));
      } finally {
        setItemActionPending(key, false);
      }
    },
    [actionPendingKeys, filters.moderationStatus, rejectReasons, setItemActionPending],
  );

  const moderationTabs = useMemo<TabsMap>(
    () => ({
      pending: {
        id: "pending",
        name: "Новые",
      },
      rejected: {
        id: "rejected",
        name: "Отклоненные",
      },
    }),
    [],
  );

  const handleModerationTabChange = useCallback((id: string) => {
    if (id !== "pending" && id !== "rejected") return;
    setRejectKey(null);
    setActionErrors({});
    setSelectedListingKeys(new Set());
    setBulkDeleteError(null);
    setFilters((current) => ({
      ...current,
      moderationStatus: id,
    }));
  }, []);

  const streamLabel =
    streamStatus === "connected"
      ? "Realtime включён"
      : streamStatus === "connecting"
        ? "Подключаем realtime"
        : streamStatus === "error"
          ? "Realtime недоступен"
          : "Realtime выключен";

  if (!canManageListings) {
    return (
      <main className="mx-auto max-w-[900px] px-3.5 py-10 md:px-6">
        <Error title="Нет доступа" description="Очередь модерации доступна только администраторам." />
      </main>
    );
  }

  return (
    <main className="mx-auto grid max-w-[1344px] gap-4 px-3.5 pb-14 pt-6 md:px-6 lg:px-10 xl:px-12">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="m-0 text-[30px] font-bold leading-[1.08] tracking-[-0.03em] text-[#1a1a1a]">
            Модерация объявлений
          </h1>
          <p className="mt-2 text-sm leading-[1.45] text-[#6b6b6b]">
            Проверяйте новые и отредактированные объявления перед публикацией.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex h-[38px] cursor-pointer items-center gap-2 rounded-md border-[1.5px] border-[#e8e8e8] bg-white px-3.5 text-[13px] font-semibold text-[#5f5f5f] transition hover:border-[#c8508a] hover:text-[#8b2a5e]"
            onClick={() => void loadListings()}
          >
            <RefreshCw size={16} />Обновить
          </button>
          <button
            type="button"
            className={`inline-flex h-[38px] cursor-pointer items-center gap-2 rounded-md border-[1.5px] px-3.5 text-[13px] font-semibold transition ${
              realtimeEnabled
                ? "border-[#c8508a] bg-[#c8508a] text-white hover:bg-[#b3407a]"
                : "border-[#e8e8e8] bg-white text-[#5f5f5f] hover:border-[#c8508a] hover:text-[#8b2a5e]"
            }`}
            onClick={() => setRealtimeEnabled((value) => !value)}
          >
            {realtimeEnabled ? <Wifi size={16} /> : <WifiOff size={16} />}
            {streamLabel}
          </button>
        </div>
      </div>

      <MarketNavigation canModerateListings={canManageListings} />

      <HHTabs
        activeTab={filters.moderationStatus}
        setActiveTab={handleModerationTabChange}
        tabs={moderationTabs}
        listClassName="!rounded-[12px] !border-[#e8e8e8] !bg-white !p-1 !shadow-none"
        triggerClassName="!min-h-[38px] !rounded-[10px] !px-4 !py-2 !text-[13px] data-[state=active]:!bg-[#fbeaf0] data-[state=active]:!text-[#8b2a5e] data-[state=active]:!shadow-none"
      />

      <section className="grid gap-3 rounded-[14px] border border-[#e8e8e8] bg-white p-3.5 shadow-[0_8px_22px_rgba(0,0,0,0.08)]">
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-[1.4fr_0.8fr_0.9fr_0.8fr]">
          <label className="grid gap-1.5">
            <span className="text-xs text-[#6b6b6b]">Поиск</span>
            <div className="relative">
              <input
                className="h-[38px] w-full rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 pr-9 text-[13px] outline-none focus:border-[#c8508a] focus:bg-white"
                value={filters.search}
                placeholder="Заголовок или адрес"
                onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9b9b9b]" size={16} />
            </div>
          </label>
          <label className="grid gap-1.5">
            <span className="text-xs text-[#6b6b6b]">Тип объекта</span>
            <select
              className="h-[38px] rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 text-[13px] outline-none focus:border-[#c8508a] focus:bg-white"
              value={filters.type}
              onChange={(event) => setFilters((current) => ({ ...current, type: event.target.value as ListingType | "all" }))}
            >
              <option value="all">Все</option>
              <option value="house">Дом</option>
              <option value="land_plot">Участок</option>
              <option value="apartment">Квартира</option>
              <option value="commercial">Коммерция</option>
            </select>
          </label>
          <label className="grid gap-1.5">
            <span className="text-xs text-[#6b6b6b]">Сделка</span>
            <select
              className="h-[38px] rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 text-[13px] outline-none focus:border-[#c8508a] focus:bg-white"
              value={filters.marketType}
              onChange={(event) => setFilters((current) => ({ ...current, marketType: event.target.value as MarketType | "all" }))}
            >
              <option value="all">Все</option>
              <option value="sale">Продажа</option>
              <option value="rent">Аренда</option>
            </select>
          </label>
          <label className="grid gap-1.5">
            <span className="text-xs text-[#6b6b6b]">Owner ID</span>
            <input
              className="h-[38px] rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 text-[13px] outline-none focus:border-[#c8508a] focus:bg-white"
              value={filters.ownerId}
              inputMode="numeric"
              onChange={(event) => setFilters((current) => ({ ...current, ownerId: event.target.value }))}
            />
          </label>
        </div>

        <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
          <label className="grid gap-1.5">
            <span className="text-xs text-[#6b6b6b]">Категория</span>
            <input
              className="h-[38px] rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 text-[13px] outline-none placeholder:text-[#a0a0a0] focus:border-[#c8508a] focus:bg-white"
              value={filters.boardCategory}
              placeholder="houses, secondary, commercial..."
              onChange={(event) => setFilters((current) => ({ ...current, boardCategory: event.target.value }))}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="text-xs text-[#6b6b6b]">Сортировка</span>
            <select
              className="h-[38px] rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 text-[13px] outline-none focus:border-[#c8508a] focus:bg-white"
              value={filters.sort}
              onChange={(event) => setFilters((current) => ({ ...current, sort: event.target.value as SortKey }))}
            >
              {Object.entries(SORT_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1.5">
            <span className="text-xs text-[#6b6b6b]">Порядок</span>
            <select
              className="h-[38px] rounded-md border border-[#e8e8e8] bg-[#f7f7f7] px-3 text-[13px] outline-none focus:border-[#c8508a] focus:bg-white"
              value={filters.sortDirection}
              onChange={(event) => setFilters((current) => ({ ...current, sortDirection: event.target.value as "asc" | "desc" }))}
            >
              <option value="desc">Сначала новые/больше</option>
              <option value="asc">Сначала старые/меньше</option>
            </select>
          </label>
        </div>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-[#6b6b6b]">
        <div>
          Найдено: <strong>{items.length}</strong>
        </div>
        <span className="rounded-full bg-[#f7f7f7] px-2.5 py-1 text-xs font-semibold text-[#6b6b6b]">
          {filters.moderationStatus === "pending" ? "Новые объявления" : "Отклоненные объявления"}
        </span>
      </div>

      {!pending && !error && items.length ? (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[#e8c5d8] bg-[#fff7fb] px-3.5 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-[#5f5f5f]">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-[#c8508a]"
                checked={allSelectableListingsSelected}
                disabled={bulkDeletePending}
                onChange={toggleAllListingSelection}
              />
              {allSelectableListingsSelected ? "Снять выбор" : "Выбрать все загруженные"}
            </label>
            <span className="text-[13px] text-[#6b6b6b]">
              Выбрано: <strong>{selectedListings.length}</strong>
              {items.length > 100 ? " (максимум 100 за один запрос)" : ""}
            </span>
          </div>
          <button
            type="button"
            className="inline-flex h-[38px] cursor-pointer items-center gap-2 rounded-md border border-rose-600 bg-rose-600 px-3.5 text-[13px] font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!selectedListings.length || bulkDeletePending}
            aria-busy={bulkDeletePending}
            onClick={() => void deleteSelectedListings()}
          >
            <Trash2 size={16} />
            {bulkDeletePending
              ? "Удаляем..."
              : `Удалить выбранные${selectedListings.length ? ` (${selectedListings.length})` : ""}`}
          </button>
          {bulkDeleteError ? <p className="m-0 w-full text-[12px] text-[#b42318]">{bulkDeleteError}</p> : null}
        </section>
      ) : null}

      {error ? (
        <Error title="Очередь недоступна" description={error} retryCallback={() => loadListings()} />
      ) : pending ? (
        <div className="grid gap-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="h-[150px] animate-pulse rounded-[14px] bg-[#ececec]" />
          ))}
        </div>
      ) : items.length ? (
        <ul className="m-0 grid list-none gap-3 p-0">
          {items.map((item) => {
            const key = getListingKey(item);
            const actionPending = actionPendingKeys.has(key);
            const rejectOpen = rejectKey === key;
            const selectedForDeletion = selectedListingKeys.has(key);
            const area = formatArea(item);

            return (
              <li
                key={key}
                className={`grid gap-3 rounded-none border-y border-[#e8e8e8] bg-white p-3.5 shadow-[0_1px_3px_rgba(0,0,0,0.07)] md:grid-cols-[160px_minmax(0,1fr)_260px] md:rounded-[16px] md:border ${
                  selectedForDeletion ? "ring-2 ring-[#c8508a]" : ""
                }`}
              >
                <div className="relative h-[150px] overflow-hidden rounded-xl bg-[#e8e8e8] md:h-full">
                  <label className="absolute left-3 top-3 z-10 grid h-8 w-8 cursor-pointer place-items-center rounded-lg border border-[#e8c5d8] bg-white shadow-[0_3px_10px_rgba(0,0,0,0.14)]">
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer accent-[#c8508a]"
                      checked={selectedForDeletion}
                      disabled={bulkDeletePending || actionPending}
                      aria-label={`Выбрать объявление «${item.title}»`}
                      onChange={() => toggleListingSelection(item)}
                    />
                  </label>
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="grid h-full place-items-center text-[13px] text-[#777]">Нет фото</div>
                  )}
                </div>

                <div className="grid content-start gap-2">
                  <div className="flex flex-wrap gap-1.5">
                    <span className="rounded-full bg-[#fbeaf0] px-2.5 py-1 text-[11px] font-semibold text-[#8b2a5e]">
                      {MARKET_LABELS[item.market_type]}
                    </span>
                    <span className="rounded-full bg-[#f2f2f2] px-2.5 py-1 text-[11px] font-semibold text-[#5f5f5f]">
                      {CATEGORY_LABELS[item.board_subcategory || ""] || CATEGORY_LABELS[item.board_category] || TYPE_LABELS[item.type]}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                        item.moderation_status === "approved"
                          ? "bg-[#f0fdf4] text-[#166534]"
                          : item.moderation_status === "rejected"
                            ? "bg-[#fef2f2] text-[#b42318]"
                            : "bg-[#fff7ed] text-[#9a3412]"
                      }`}
                    >
                      {MODERATION_LABELS[item.moderation_status]}
                    </span>
                  </div>
                  <h2 className="m-0 text-[18px] font-bold leading-[1.25] text-[#1a1a1a]">{item.title}</h2>
                  <div className="text-[22px] font-bold leading-none text-[#8b2a5e]">{getListingPrice(item)}</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs leading-5 text-[#6b6b6b]">
                    <span>ID {item.id}</span>
                    <span>Автор {item.author_id || item.owner_id || "—"}</span>
                    {area ? <span>{area}</span> : null}
                    <span>Отправлено: {formatDate(item.moderation_submitted_at)}</span>
                    <span>Обновлено: {formatDate(item.updated_at)}</span>
                  </div>
                  {item.moderation_reason ? (
                    <div className="rounded-lg border border-[#f5c2c7] bg-[#fff5f5] px-3 py-2 text-[13px] leading-5 text-[#9f1239]">
                      Причина отказа: {item.moderation_reason}
                    </div>
                  ) : null}
                  {actionErrors[key] ? <div className="text-[13px] text-[#b42318]">{actionErrors[key]}</div> : null}
                </div>

                <div className="grid content-start gap-2">
                  <button
                    type="button"
                    className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md border border-[#e8e8e8] bg-white px-3 text-[13px] font-semibold text-[#555] transition hover:border-[#c8508a] hover:text-[#8b2a5e]"
                    onClick={() => navigate(`/classifieds/${item.type}/${item.id}`)}
                  >
                    <Eye size={16} />Открыть
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md border border-emerald-600 bg-emerald-600 px-3 text-[13px] font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-60"
                    disabled={bulkDeletePending || actionPending}
                    onClick={() => void approveListing(item)}
                  >
                    <Check size={16} />Принять
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md border border-rose-300 bg-white px-3 text-[13px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-wait disabled:opacity-60"
                    disabled={bulkDeletePending || actionPending}
                    onClick={() => setRejectKey((current) => (current === key ? null : key))}
                  >
                    <X size={16} />Отклонить
                  </button>
                  {rejectOpen ? (
                    <div className="grid gap-2 rounded-xl border border-[#f5c2c7] bg-[#fff7f7] p-2.5">
                      <textarea
                        className="min-h-[92px] resize-y rounded-md border border-[#efc5d8] bg-white p-2 text-[13px] outline-none focus:border-[#c8508a]"
                        placeholder="Причина отказа, необязательно"
                        value={rejectReasons[key] || ""}
                        onChange={(event) =>
                          setRejectReasons((current) => ({
                            ...current,
                            [key]: event.target.value,
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="h-[36px] rounded-md border border-rose-600 bg-rose-600 px-3 text-[13px] font-semibold text-white transition hover:bg-rose-700 disabled:cursor-wait disabled:opacity-60"
                        disabled={bulkDeletePending || actionPending}
                        onClick={() => void rejectListing(item)}
                      >
                        {actionPending ? "Отклоняем..." : "Подтвердить отказ"}
                      </button>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="rounded-[14px] border border-dashed border-[#e8e8e8] bg-white px-3.5 py-[34px] text-center text-[#6b6b6b]">
          Объявлений с выбранными параметрами нет
        </div>
      )}

      {pagination.has_more || loadMoreError ? (
        <div className="grid justify-items-center gap-2 pt-2">
          {loadMoreError ? <p className="m-0 text-[13px] text-[#b42318]">{loadMoreError}</p> : null}
          <button
            type="button"
            className="min-w-[170px] cursor-pointer rounded-md border border-[#c8508a] bg-white px-4 py-2 text-sm font-semibold text-[#8b2a5e] transition hover:bg-[#fff2f8] disabled:cursor-wait disabled:opacity-60"
            disabled={loadMorePending || !pagination.next_cursor}
            onClick={() => loadListings(pagination.next_cursor)}
          >
            {loadMorePending ? "Загружаем..." : loadMoreError ? "Повторить" : "Показать ещё"}
          </button>
        </div>
      ) : null}
    </main>
  );
};

const mapState = (state: RootState) => ({
  user: selectUserInfo(state),
  userData: selectUserData(state),
  userToken: selectUserToken(state),
});

export const ClassifiedsModeration = connect(mapState)(_ClassifiedsModeration);
